import type { DocBlock, DocBlockKind, DocInline } from './paginate';

/**
 * Converts a static HTML document (the Termly legal export in
 * `public/toc.html`) into plain `DocBlock`s that can be paginated and
 * rendered as React text — no `dangerouslySetInnerHTML`. Every visible text
 * node is kept, in document order; only `<style>` / `<script>` are dropped.
 * Links are kept when they are http(s), mailto or in-document anchors.
 */

const BLOCK_TAGS = new Set([
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'BODY',
  'DD',
  'DIV',
  'DL',
  'DT',
  'FOOTER',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'LI',
  'MAIN',
  'OL',
  'P',
  'SECTION',
  'TABLE',
  'TBODY',
  'TD',
  'TH',
  'THEAD',
  'TR',
  'UL',
]);
const BLOCK_SELECTOR = Array.from(BLOCK_TAGS)
  .filter(t => t !== 'BODY')
  .map(t => t.toLowerCase())
  .join(',');

const safeHref = (raw: string | null): string | undefined => {
  if (!raw) return undefined;
  const href = raw.trim();
  if (/^#[\w-]+$/.test(href)) return href;
  if (/^(https?:|mailto:)/i.test(href)) return href;
  return undefined;
};

const classify = (el: Element): DocBlockKind => {
  // Only look inside leaf blocks; a mixed container's nested blocks are
  // classified on their own.
  const leaf = !el.querySelector(BLOCK_SELECTOR);
  const has = (cls: string) =>
    el.matches(`[data-custom-class='${cls}']`) ||
    (leaf && !!el.querySelector(`[data-custom-class='${cls}']`));
  if (has('title') || el.tagName === 'H1') return 'title';
  if (has('heading_1') || el.tagName === 'H2') return 'h1';
  if (has('heading_2') || /^H[3-6]$/.test(el.tagName)) return 'h2';
  if (has('subtitle')) return 'subtitle';
  if (el.closest('li')) return 'li';
  return 'p';
};

const isBlock = (node: Node): node is Element =>
  node.nodeType === 1 && BLOCK_TAGS.has((node as Element).tagName);

export const parseDocumentHtml = (html: string): DocBlock[] => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll('style, script, noscript, template').forEach(n => {
    n.remove();
  });

  const blocks: DocBlock[] = [];
  let pendingAnchors: string[] = [];

  const noteAnchor = (el: Element) => {
    const id = el.getAttribute('id') || el.getAttribute('name');
    if (id && !pendingAnchors.includes(id)) pendingAnchors.push(id);
  };

  const collectInline = (
    node: Node,
    out: DocInline[],
    ctx: { href?: string; strong?: boolean }
  ) => {
    if (node.nodeType === 3) {
      const text = (node.textContent || '').replace(/\s+/g, ' ');
      if (text) out.push({ text, ...ctx });
      return;
    }
    if (node.nodeType !== 1) return;
    const el = node as Element;
    noteAnchor(el);
    if (el.tagName === 'BR') {
      out.push({ text: ' ', ...ctx });
      return;
    }
    const next = { ...ctx };
    if (el.tagName === 'A') {
      const href = safeHref(el.getAttribute('href'));
      if (href) next.href = href;
    }
    if (el.tagName === 'STRONG' || el.tagName === 'B') next.strong = true;
    el.childNodes.forEach(child => collectInline(child, out, next));
  };

  const emit = (inlines: DocInline[], kind: DocBlockKind) => {
    // Merge neighbouring runs with the same formatting, normalise spaces.
    const merged: DocInline[] = [];
    inlines.forEach(run => {
      const last = merged[merged.length - 1];
      if (last && last.href === run.href && last.strong === run.strong) {
        last.text += run.text;
      } else {
        merged.push({ ...run });
      }
    });
    merged.forEach(run => {
      run.text = run.text.replace(/\s+/g, ' ');
    });
    if (merged.length) {
      merged[0].text = merged[0].text.replace(/^\s+/, '');
      const end = merged[merged.length - 1];
      end.text = end.text.replace(/\s+$/, '');
    }
    const clean = merged.filter(r => r.text.length > 0);
    if (!clean.some(r => r.text.trim())) return;
    const block: DocBlock = { kind, inlines: clean };
    if (pendingAnchors.length) {
      block.anchors = pendingAnchors;
      pendingAnchors = [];
    }
    blocks.push(block);
  };

  const walk = (container: Element) => {
    noteAnchor(container);
    let run: DocInline[] = [];
    const flush = () => {
      if (run.length) emit(run, classify(container));
      run = [];
    };
    container.childNodes.forEach(child => {
      const nested =
        child.nodeType === 1 &&
        !!(child as Element).querySelector(BLOCK_SELECTOR);
      if (isBlock(child) || nested) {
        flush();
        walk(child as Element);
      } else {
        collectInline(child, run, {});
      }
    });
    flush();
  };

  walk(doc.body);

  if (pendingAnchors.length && blocks.length) {
    const last = blocks[blocks.length - 1];
    last.anchors = [...(last.anchors || []), ...pendingAnchors];
  }
  return blocks;
};
